import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  ButtonStyleHelper,
  ConfigService,
  ConsentTool,
  DEFAULT_CONFIG,
  InformationService,
  IUiButtonSettings,
  SystemService
} from 'libs/shared/src';
import * as CookieConsent from 'vanilla-cookieconsent';

@Injectable({
  providedIn: 'root'
})
export class FooterService {
  // Services
  public readonly config = inject(ConfigService);
  private readonly buttonStyleHelper = inject(ButtonStyleHelper);
  private readonly informationService = inject(InformationService);
  private readonly system = inject(SystemService);

  // Properties & Signals
  /** Link to the revocation page — undefined if no link target is configured. */
  public readonly revocationLink: Signal<string | undefined> = computed(() => {
    return this.system.getLink(this.informationService.init()?.linkTargets?.revocation);
  });
  /**
   * Link auf die AGB für den gesetzlichen Gewährleistungshinweis — undefined, wenn kein Linkziel `agb` gepflegt ist.
   * Der Hinweis selbst wird immer angezeigt (EmpCo), nur der Link „Mehr erfahren“ hängt an diesem Linkziel.
   */
  public readonly warrantyLink: Signal<string | undefined> = computed(() => {
    return this.system.getLink(this.informationService.init()?.linkTargets?.agb);
  });
  /** Whether the revocation block should be rendered — activated in the config and a link target available. */
  public readonly showRevocation: Signal<boolean> = computed(() => {
    const active = this.config.config()?.footer?.infoarea?.revocation?.active ?? DEFAULT_CONFIG.footer?.infoarea?.revocation?.active;
    return !!active && !!this.revocationLink();
  });
  /** Whether at least one social media link is set — otherwise the whole social media block (incl. heading) is hidden. */
  public readonly hasSocialMediaLinks: Signal<boolean> = computed(() => {
    return Object.values(this.informationService.init()?.socialMediaLinks ?? {}).some((link) => !!link);
  });
  /** Whether at least one app link is set — otherwise the whole app links block (incl. heading) is hidden. */
  public readonly hasAppLinks: Signal<boolean> = computed(() => {
    return Object.values(this.informationService.init()?.appLinks ?? {}).some((link) => !!link?.trim());
  });
  /** Whether at least one payment method has a picture — otherwise the whole payment block (incl. heading) is hidden. */
  public readonly hasPaymentMethods: Signal<boolean> = computed(() => {
    return (this.informationService.init()?.paymentMethods ?? []).some((method) => !!method.picture);
  });
  /** Whether at least one shipping method has a picture — otherwise the whole delivery block (incl. heading) is hidden. */
  public readonly hasShippingMethods: Signal<boolean> = computed(() => {
    return (this.informationService.init()?.shippingMethods ?? []).some((method) => !!method.picture);
  });

  /** Resolved button settings: schema defaults → global uiElements → footer revocation override */
  private readonly buttonSettings: Signal<Partial<IUiButtonSettings>> = computed(() => {
    const defaults = DEFAULT_CONFIG.uiElements?.buttons;
    const global = this.config.config()?.uiElements?.buttons;
    const local = this.config.config()?.footer?.infoarea?.revocationButton;
    // Only spread non-null local values — null means "inherit from global" (field was never explicitly set)
    const effectiveLocal = local
      ? (Object.fromEntries(
          Object.entries(local).filter(
            ([, v]) => v != null && (typeof v !== 'object' || Object.values(v as Record<string, unknown>).some((sv) => sv != null))
          )
        ) as Partial<IUiButtonSettings>)
      : {};
    return { ...defaults, ...global, ...effectiveLocal };
  });

  // Getters & Setters
  /** Current calendar year, used for the footer copyright notice. */
  public get currentYear(): number {
    return new Date().getFullYear();
  }

  /** Primary-button CSS classes for the revocation button, matching the site's global button styling. */
  public get revocationButtonClasses(): string {
    return this.buttonStyleHelper.getPrimaryButtonClasses(this.buttonSettings());
  }

  /** Inline button styles (padding, font-size) for the revocation button. */
  public get revocationButtonStyles(): Record<string, string> {
    return this.buttonStyleHelper.getButtonStyles(this.buttonSettings());
  }

  /**
   * Whether the tenant uses the vanilla-cookieconsent banner. Other consent tools never call
   * `CookieConsent.run()`, so a preferences button would be dead (and throw when clicked).
   */
  public get cookieBannerActive(): boolean {
    return this.informationService.init()?.consentTool === ConsentTool.CookieBanner;
  }

  // Methods
  /** Opens the cookie preferences dialog so already-consented users can review or change their choices. */
  public showCookiePreferences(): void {
    // `run()` happens asynchronously after the component scripts load; before that the library
    // has no DOM and `showPreferences()` would throw on an undefined modal element.
    if (!CookieConsent.getConfig('categories')) return;
    CookieConsent.showPreferences();
  }
}
